import type { CliE2eeRuntime } from '../../lib/e2ee/index.js';
import { buildFirehoseLogChunkMessage, buildFirehoseSnapshotMessage } from './firehose-messages.js';
import type { PreviewEnvironmentLogTails } from './types.js';

export class PreviewEnvironmentFirehoseSink {
  private logViewerIdsByEnvironmentId = new Map<string, Set<string>>();
  private firehoseSend: ((msg: Record<string, unknown>) => void) | null = null;

  constructor(
    private readonly options: {
      getTails: (environmentId: string) => PreviewEnvironmentLogTails;
      isPipedCaptureEnabled: (environmentId: string) => boolean;
      e2ee?: CliE2eeRuntime;
    }
  ) {}

  attach(send: (msg: Record<string, unknown>) => void): void {
    this.firehoseSend = send;
  }

  detach(): void {
    this.firehoseSend = null;
    this.logViewerIdsByEnvironmentId.clear();
  }

  openLogViewer(environmentId: string, viewerId: string): void {
    let viewers = this.logViewerIdsByEnvironmentId.get(environmentId);
    if (!viewers) {
      viewers = new Set();
      this.logViewerIdsByEnvironmentId.set(environmentId, viewers);
    }
    viewers.add(viewerId);
    this.sendSnapshot(environmentId, viewerId);
  }

  closeLogViewer(environmentId: string, viewerId: string): void {
    const viewers = this.logViewerIdsByEnvironmentId.get(environmentId);
    if (!viewers) return;
    viewers.delete(viewerId);
    if (viewers.size === 0) this.logViewerIdsByEnvironmentId.delete(environmentId);
  }

  refreshLogViewers(environmentId: string): void {
    const viewers = this.logViewerIdsByEnvironmentId.get(environmentId);
    if (!viewers || viewers.size === 0) return;
    for (const viewerId of viewers) {
      this.sendSnapshot(environmentId, viewerId);
    }
  }

  pushLogChunk(environmentId: string, stream: 'stdout' | 'stderr', chunk: Buffer): void {
    if (!this.hasLogViewers(environmentId)) return;
    if (!this.options.isPipedCaptureEnabled(environmentId)) return;
    if (!this.firehoseSend) return;
    const text = chunk.toString('utf8');
    setImmediate(() => {
      if (!this.firehoseSend) return;
      this.firehoseSend(buildFirehoseLogChunkMessage({ environmentId, stream, text, e2ee: this.options.e2ee }));
    });
  }

  private hasLogViewers(environmentId: string): boolean {
    return (this.logViewerIdsByEnvironmentId.get(environmentId)?.size ?? 0) > 0;
  }

  private sendSnapshot(environmentId: string, viewerId: string): void {
    const payload = buildFirehoseSnapshotMessage({
      environmentId,
      viewerId,
      tails: this.options.getTails(environmentId),
      e2ee: this.options.e2ee,
    });
    setImmediate(() => {
      const send = this.firehoseSend;
      if (!send) return;
      send(payload);
    });
  }
}
